// src/pages/TriageReportPage.js
//
// Full-page version of the triage report that HealthRecordsTab used to open as
// a modal. Route: /patients/:id/reports/:recordId. The report component and its
// tabs are unchanged — TriageReportModal just renders inline here (asPage).
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import TriageReportModal from '../components/healthrecords/TriageReportModal';
import { patientAPI, healthRecordAPI } from '../services/api';

export default function TriageReportPage() {
  const { id: patientId, recordId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError('');
    Promise.all([
      patientAPI.get(patientId),
      healthRecordAPI.get(patientId, recordId),
      healthRecordAPI.getTriageResult(patientId, recordId),
      healthRecordAPI.getTriageHistory(patientId).catch(() => ({ data: [] })),
    ]).then(([p, rec, triage, history]) => {
      if (cancelled) return;
      setData({
        patient: p.data,
        record: rec.data,
        triage: triage.data,
        // Everything except this record; the report keeps only the draws before it.
        history: (history.data || []).filter(t => t.healthRecordId !== recordId),
      });
    }).catch((err) => {
      if (cancelled) return;
      // Never fall back to the list-summary shape: it has no markers or engine
      // scores, so it renders a confidently empty clinical report.
      console.error('Failed to load triage report:', err);
      setError('Could not load the full report for this document. Please retry — '
        + 'do not rely on a partially loaded report.');
    });
    return () => { cancelled = true; };
  }, [patientId, recordId]);

  const backToRecords = () => navigate(`/patients/${patientId}?tab=Health+Records`);

  const breadcrumbs = [
    { label: 'Patients', to: '/patients' },
    { label: data?.patient ? `${data.patient.firstName || ''} ${data.patient.lastName || ''}`.trim() || 'Patient' : 'Patient', to: `/patients/${patientId}?tab=Health+Records` },
    { label: 'Health report' },
  ];

  return (
    <AppShell breadcrumbs={breadcrumbs}>
      <span className="back-link fade-in-up" onClick={backToRecords}>
        ← Health records
      </span>

      {error && (
        <div className="alert alert-error" role="alert">{error}</div>
      )}

      {!data && !error && (
        <div className="loading-state" style={{ padding: 40 }}>
          <div className="dot-loader"><span /><span /><span /></div>
          <div style={{ fontSize: 13 }}>Loading report…</div>
        </div>
      )}

      {data && (
        <TriageReportModal
          asPage
          triageResult={data.triage}
          healthRecord={data.record}
          patient={data.patient}
          triageHistory={data.history}
          onClose={backToRecords}
          onMessagePatient={() => navigate(`/patients/${patientId}/chat`)}
          onReevaluated={(triageResultId, reevaluation) => {
            setData(prev => (prev && prev.triage?.id === triageResultId
              ? { ...prev, triage: { ...prev.triage, consensusRiskLevel: reevaluation.newRiskLevel, consensusRiskScore: reevaluation.newRiskScore } }
              : prev));
          }}
        />
      )}
    </AppShell>
  );
}
